import React from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {SearchBoldIcon} from "@pg-design/icons-module";
import {noop} from "@pg-mono/nodash";
import {NAVIGATION_MOBILE_TEST_IDS} from "@pg-mono/test-ids";

import {MobileOptionsModal} from "../../../atoms/MobileOptionsModal";
import {ISearchInputValue} from "../../../search/components/ISearchInputValue";
import {ISearchAutocompleteFormValues, SearchAutocomplete} from "../../../search/components/search_autocomplete/SearchAutocomplete";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {SearchTab} from "../../../search/types/SearchTab";
import {addNewRegionToCurrentRegions} from "../../../search/utils/add_new_region_to_current_regions";
import {HomepageHeroMobileSearchPlaceholder} from "./HomepageHeroMobileSearchPlaceholder";
import {HomepageSearchDropdownList} from "./HomepageSearchDropdownList";
import {IHomepageSearchViewProps} from "./HomepageSearchTypes";

const createEmptySearchValue = (): ISearchInputValue => ({label: "", tabType: null});
const createMobileModalSearchValue = (searchValue: ISearchInputValue | undefined): ISearchInputValue => {
    if (searchValue?.tabType === SearchTab.Regions) {
        return {...searchValue, label: ""};
    }

    return createEmptySearchValue();
};

const createMobileModalFormValues = (formValues: SearchFormValuesType): SearchFormValuesType => ({
    ...formValues,
    search: createMobileModalSearchValue(formValues.search)
});

export const HomepageHeroMobileSearch = (props: IHomepageSearchViewProps) => {
    const shouldShowModalSearchButton = props.mobileSelectionMode !== "selectAndClose";
    const shouldUseModalDraft = props.mobileSelectionMode === "selectAndClose";
    const [mobileModalFormValues, setMobileModalFormValues] = React.useState<SearchFormValuesType | null>(null);

    const openMobileSearchModal = () => {
        if (shouldUseModalDraft) {
            setMobileModalFormValues(createMobileModalFormValues(props.formValues));
        }

        props.setMobileSearchActive(true);
    };

    const closeMobileSearchModal = () => {
        setMobileModalFormValues(null);
        props.setMobileSearchActive(false);
    };

    const onMobileModalChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        const nextValues = {...(mobileModalFormValues ?? createMobileModalFormValues(props.formValues)), ...values};
        const isRegionRemoval = isRegionDraftRemoval(values.search, mobileModalFormValues?.search);
        setMobileModalFormValues(nextValues);

        if (isRegionRemoval) {
            props.onChange(getCommittedModalValues(values, props.formValues.search, !!props.disableMultiRegion));
            return;
        }

        if (isResolvedSearchValue(values.search)) {
            props.onMobileHeroChange(getCommittedModalValues(values, props.formValues.search, !!props.disableMultiRegion));
            setMobileModalFormValues(null);
        }
    };

    const onMobileModalAfterChange = (name?: string, value?: unknown) => {
        if (name === "search" && !isResolvedSearchValue(value as ISearchInputValue | undefined)) {
            return;
        }

        props.onMobileHeroAfterChange(name, value);
    };

    const modalFormValues = shouldUseModalDraft ? (mobileModalFormValues ?? createMobileModalFormValues(props.formValues)) : props.formValues;
    const modalOnChange = shouldUseModalDraft ? onMobileModalChange : props.onMobileHeroChange;
    const modalOnAfterChange = shouldUseModalDraft ? onMobileModalAfterChange : props.onMobileHeroAfterChange;

    return (
        <div className={heroMobileWrapperStyle}>
            <HomepageHeroMobileSearchPlaceholder searchValue={props.formValues.search} onClick={openMobileSearchModal} />
            <Button
                type="button"
                variant="filled_primary"
                size="medium"
                className={mobileSearchButtonStyle}
                onClick={props.onSearchSubmit}
                disabled={props.isSearchButtonDisabled}
                dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.SEARCH_BUTTON}
            >
                SZUKAJ
            </Button>

            <MobileOptionsModal modalHeading="Wyszukiwanie" isOpen={props.isMobileSearchActive} onModalClose={closeMobileSearchModal}>
                <div className={mobileModalSearchWrapperStyle}>
                    <SearchAutocomplete
                        values={modalFormValues}
                        onChange={modalOnChange}
                        onAfterChange={modalOnAfterChange}
                        onSubmit={props.autocompleteSubmit}
                        onDropdownStatusChange={noop}
                        onAfterTabSwitch={props.onAfterTabSwitch}
                        fetchAllSearchLists={props.fetchAllSearchLists}
                        autoFocus={props.autoFocus ?? true}
                        isInModal
                        hideArrowIcon
                        disableMultiRegion={!!props.disableMultiRegion}
                        inputIcon={<SearchBoldIcon size="2.4" />}
                        minWidth="auto"
                        renderDropdownList={HomepageSearchDropdownList}
                    />
                    {shouldShowModalSearchButton ? (
                        <Button
                            type="button"
                            variant="filled_primary"
                            size="medium"
                            className={modalSearchButtonStyle}
                            onClick={props.onSearchSubmit}
                            disabled={props.isSearchButtonDisabled}
                            dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.SEARCH_BUTTON}
                        >
                            SZUKAJ
                        </Button>
                    ) : null}
                </div>
            </MobileOptionsModal>
        </div>
    );
};

const getCommittedModalValues = (
    values: Partial<ISearchAutocompleteFormValues>,
    currentSearchValue: ISearchInputValue | undefined,
    disableMultiRegion: boolean
) => {
    if (values.search?.tabType !== SearchTab.Regions || currentSearchValue?.tabType !== SearchTab.Regions) {
        return values;
    }

    const selectedRegion = values.search.regions[0];
    if (!selectedRegion) {
        return values;
    }

    return {
        ...values,
        search: {
            ...values.search,
            regions: disableMultiRegion ? values.search.regions : addNewRegionToCurrentRegions(currentSearchValue.regions, selectedRegion)
        }
    };
};

const isRegionDraftRemoval = (nextSearchValue: ISearchInputValue | undefined, currentModalSearchValue: ISearchInputValue | undefined) => {
    if (nextSearchValue?.tabType !== SearchTab.Regions || currentModalSearchValue?.tabType !== SearchTab.Regions) {
        return false;
    }

    return nextSearchValue.regions.length < currentModalSearchValue.regions.length;
};

const isResolvedSearchValue = (searchValue?: ISearchInputValue) => {
    switch (searchValue?.tabType) {
        case SearchTab.Regions:
            return searchValue.regions.length > 0;
        case SearchTab.Offers:
            return Boolean(searchValue.offer);
        case SearchTab.Places:
            return Boolean(searchValue.place && searchValue.location);
        case SearchTab.Vendors:
            return Boolean(searchValue.vendor);
        default:
            return false;
    }
};

const heroMobileWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 1.5,
    width: "100%",
    maxWidth: "none",

    md: {
        display: "none"
    }
});

const mobileSearchButtonStyle = css({
    width: "100%",
    fontWeight: 700,
    textTransform: "uppercase"
});

const mobileModalSearchWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 2
});

const modalSearchButtonStyle = css({
    width: "100%",
    borderRadius: "999px",
    fontWeight: 700,
    textTransform: "uppercase"
});
import React from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {SearchBoldIcon} from "@pg-design/icons-module";
import {noop} from "@pg-mono/nodash";
import {NAVIGATION_MOBILE_TEST_IDS} from "@pg-mono/test-ids";

import {MobileOptionsModal} from "../../../atoms/MobileOptionsModal";
import {ISearchInputValue} from "../../../search/components/ISearchInputValue";
import {ISearchAutocompleteFormValues, SearchAutocomplete} from "../../../search/components/search_autocomplete/SearchAutocomplete";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {SearchTab} from "../../../search/types/SearchTab";
import {addNewRegionToCurrentRegions} from "../../../search/utils/add_new_region_to_current_regions";
import {HomepageHeroMobileSearchPlaceholder} from "./HomepageHeroMobileSearchPlaceholder";
import {HomepageSearchDropdownList} from "./HomepageSearchDropdownList";
import {IHomepageSearchViewProps} from "./HomepageSearchTypes";

const createEmptySearchValue = (): ISearchInputValue => ({label: "", tabType: null});
const createMobileModalSearchValue = (searchValue: ISearchInputValue | undefined): ISearchInputValue => {
    if (searchValue?.tabType === SearchTab.Regions) {
        return {...searchValue, label: ""};
    }

    return createEmptySearchValue();
};

const createMobileModalFormValues = (formValues: SearchFormValuesType): SearchFormValuesType => ({
    ...formValues,
    search: createMobileModalSearchValue(formValues.search)
});

export const HomepageHeroMobileSearch = (props: IHomepageSearchViewProps) => {
    const shouldShowModalSearchButton = props.mobileSelectionMode !== "selectAndClose";
    const shouldUseModalDraft = props.mobileSelectionMode === "selectAndClose";
    const [mobileModalFormValues, setMobileModalFormValues] = React.useState<SearchFormValuesType | null>(null);

    const openMobileSearchModal = () => {
        if (shouldUseModalDraft) {
            setMobileModalFormValues(createMobileModalFormValues(props.formValues));
        }

        props.setMobileSearchActive(true);
    };

    const closeMobileSearchModal = () => {
        setMobileModalFormValues(null);
        props.setMobileSearchActive(false);
    };

    const onMobileModalChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        const nextValues = {...(mobileModalFormValues ?? createMobileModalFormValues(props.formValues)), ...values};
        const isRegionRemoval = isRegionDraftRemoval(values.search, mobileModalFormValues?.search);
        setMobileModalFormValues(nextValues);

        if (isRegionRemoval) {
            props.onChange(getCommittedModalValues(values, props.formValues.search, !!props.disableMultiRegion));
            return;
        }

        if (isResolvedSearchValue(values.search)) {
            props.onMobileHeroChange(getCommittedModalValues(values, props.formValues.search, !!props.disableMultiRegion));
            setMobileModalFormValues(null);
        }
    };

    const onMobileModalAfterChange = (name?: string, value?: unknown) => {
        if (name === "search" && !isResolvedSearchValue(value as ISearchInputValue | undefined)) {
            return;
        }

        props.onMobileHeroAfterChange(name, value);
    };

    const modalFormValues = shouldUseModalDraft ? (mobileModalFormValues ?? createMobileModalFormValues(props.formValues)) : props.formValues;
    const modalOnChange = shouldUseModalDraft ? onMobileModalChange : props.onMobileHeroChange;
    const modalOnAfterChange = shouldUseModalDraft ? onMobileModalAfterChange : props.onMobileHeroAfterChange;

    return (
        <div className={heroMobileWrapperStyle}>
            <HomepageHeroMobileSearchPlaceholder searchValue={props.formValues.search} onClick={openMobileSearchModal} />
            <Button
                type="button"
                variant="filled_primary"
                size="medium"
                className={mobileSearchButtonStyle}
                onClick={props.onSearchSubmit}
                disabled={props.isSearchButtonDisabled}
                dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.SEARCH_BUTTON}
            >
                SZUKAJ
            </Button>

            <MobileOptionsModal modalHeading="Wyszukiwanie" isOpen={props.isMobileSearchActive} onModalClose={closeMobileSearchModal}>
                <div className={mobileModalSearchWrapperStyle}>
                    <SearchAutocomplete
                        values={modalFormValues}
                        onChange={modalOnChange}
                        onAfterChange={modalOnAfterChange}
                        onSubmit={props.autocompleteSubmit}
                        onDropdownStatusChange={noop}
                        onAfterTabSwitch={props.onAfterTabSwitch}
                        fetchAllSearchLists={props.fetchAllSearchLists}
                        autoFocus={props.autoFocus ?? true}
                        isInModal
                        hideArrowIcon
                        disableMultiRegion={!!props.disableMultiRegion}
                        inputIcon={<SearchBoldIcon size="2.4" />}
                        minWidth="auto"
                        renderDropdownList={HomepageSearchDropdownList}
                    />
                    {shouldShowModalSearchButton ? (
                        <Button
                            type="button"
                            variant="filled_primary"
                            size="medium"
                            className={modalSearchButtonStyle}
                            onClick={props.onSearchSubmit}
                            disabled={props.isSearchButtonDisabled}
                            dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.SEARCH_BUTTON}
                        >
                            SZUKAJ
                        </Button>
                    ) : null}
                </div>
            </MobileOptionsModal>
        </div>
    );
};

const getCommittedModalValues = (
    values: Partial<ISearchAutocompleteFormValues>,
    currentSearchValue: ISearchInputValue | undefined,
    disableMultiRegion: boolean
) => {
    if (values.search?.tabType !== SearchTab.Regions || currentSearchValue?.tabType !== SearchTab.Regions) {
        return values;
    }

    const selectedRegion = values.search.regions[0];
    if (!selectedRegion) {
        return values;
    }

    return {
        ...values,
        search: {
            ...values.search,
            regions: disableMultiRegion ? values.search.regions : addNewRegionToCurrentRegions(currentSearchValue.regions, selectedRegion)
        }
    };
};

const isRegionDraftRemoval = (nextSearchValue: ISearchInputValue | undefined, currentModalSearchValue: ISearchInputValue | undefined) => {
    if (nextSearchValue?.tabType !== SearchTab.Regions || currentModalSearchValue?.tabType !== SearchTab.Regions) {
        return false;
    }

    return nextSearchValue.regions.length < currentModalSearchValue.regions.length;
};

const isResolvedSearchValue = (searchValue?: ISearchInputValue) => {
    switch (searchValue?.tabType) {
        case SearchTab.Regions:
            return searchValue.regions.length > 0;
        case SearchTab.Offers:
            return Boolean(searchValue.offer);
        case SearchTab.Places:
            return Boolean(searchValue.place && searchValue.location);
        case SearchTab.Vendors:
            return Boolean(searchValue.vendor);
        default:
            return false;
    }
};

const heroMobileWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 1.5,
    width: "100%",
    maxWidth: "none",

    md: {
        display: "none"
    }
});

const mobileSearchButtonStyle = css({
    width: "100%",
    fontWeight: 700,
    textTransform: "uppercase"
});

const mobileModalSearchWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 2
});

const modalSearchButtonStyle = css({
    width: "100%",
    borderRadius: "999px",
    fontWeight: 700,
    textTransform: "uppercase"
});
